import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { trpcConsumerGetCalendarDays } from '@trashlab/api/src/generated/hooks';
import {
  CalendarForecastRequirement,
  DateInfo,
  ProductForecast,
  Product,
  ForecastData,
  ForecastDictionary,
} from '@trashlab/api/src/domains/consumer/types/calendar';

export function useRentalCalendar({
  tenantId,
  value,
  onChange,
  daysLimit,
  onDaysOverChange,
  pickupDateOptional = false,
  forecastRequirement,
  startRentalFromDeliveryDate = false,
  blockSameDayOrder = false,
  sameDayOrderCutoffTime,
  blockedDaysFromToday = 0,
  countInactiveDaysAsRentalDays = false,
}: {
  tenantId: string;
  value?: [Date | null, Date | null];
  onChange?: (value: [Date | null, Date | null]) => void;
  daysLimit?: number;
  onDaysOverChange?: (daysOver: number) => void;
  pickupDateOptional?: boolean;
  startRentalFromDeliveryDate?: boolean;
  blockSameDayOrder?: boolean;
  sameDayOrderCutoffTime?: string;
  blockedDaysFromToday?: number;
  countInactiveDaysAsRentalDays?: boolean;
  forecastRequirement: CalendarForecastRequirement;
}) {
  const { productId } = forecastRequirement;
  const blockDatesWithoutInventory =
    forecastRequirement.blockDatesWithoutInventory ?? false;
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [selectedRange, setSelectedRange] = useState<
    [Date | null, Date | null]
  >(value || [null, null]);

  // Create stable date references to avoid infinite loops
  const today = useMemo(() => DateTime.now().startOf('day'), []);
  const now = useMemo(() => DateTime.now(), []);

  // Convert Date → YYYY-MM-DD string for the API (fully timezoneless)
  const dateToStr = (d: Date | null) =>
    d ? DateTime.fromJSDate(d).toISODate() ?? null : null;

  const rangeForQuery = value || selectedRange;
  const { data, isLoading } = trpcConsumerGetCalendarDays.useQuery(
    {
      tenantId,
      ...forecastRequirement,
      startRentalFromDeliveryDate,
      blockSameDayOrder,
      sameDayOrderCutoffTime,
      blockedDaysFromToday,
      countInactiveDaysAsRentalDays,
      daysLimit,
      selectedRange: [dateToStr(rangeForQuery[0]), dateToStr(rangeForQuery[1])],
      hoveredDate: dateToStr(hoveredDate),
    },
    {
      keepPreviousData: true,
    },
  );

  const businessHours = data?.businessHours;
  const specialDates = data?.businessSpecialDates;
  const forecast: ForecastData = {
    forecast: data?.forecast || {},
    products: data?.products || [],
  };

  const getDateInfo = useCallback(
    (date: Date): DateInfo => {
      const dateStr = DateTime.fromJSDate(date).toISODate() || '';
      const specialDate = specialDates?.find((sd) =>
        sd.date.startsWith(dateStr),
      );
      const dayOfWeek = date.getDay();
      const businessHour = businessHours?.find(
        (bh) => bh.dayOfWeek === dayOfWeek,
      );
      const isPastDay = DateTime.fromJSDate(date) < today;
      const isToday = DateTime.fromJSDate(date).hasSame(now, 'day');

      const forecastForDate = forecast?.forecast?.[dateStr];
      const productForecast = productId
        ? forecastForDate?.[productId] || undefined
        : undefined;
      const hasInventory =
        !productId || !forecastForDate || (productForecast?.count ?? 0) > 0;

      return {
        dateStr,
        specialDate,
        businessHour,
        isPastDay,
        isToday,
        hasInventory,
        forecastForDate,
        productForecast,
      };
    },
    [businessHours, forecast.forecast, productId, specialDates, today, now],
  );

  const isDateClosed = useCallback(
    (date: Date) => {
      const { specialDate, businessHour, hasInventory } = getDateInfo(date);
      return (
        (businessHour && businessHour.status === 'CLOSED') ||
        specialDate !== undefined ||
        (blockDatesWithoutInventory && !hasInventory)
      );
    },
    [blockDatesWithoutInventory, getDateInfo],
  );

  const isDateUnavailable = useCallback(
    (date: Date) => {
      const { isPastDay, isToday } = getDateInfo(date);
      // Only block today if blockSameDayOrder is true AND we've passed the cutoff (if any)
      const isTodayBlocked =
        isToday && blockSameDayOrder && (data?.isTodayPastCutoff ?? true);

      // Check if date is within blocked days from today
      const isBlockedFromToday = (() => {
        if (blockedDaysFromToday <= 0) return false;
        const dateTime = DateTime.fromJSDate(date).startOf('day');
        const diffInDays = dateTime.diff(today, 'days').days;
        // Block days from day 1 up to and including blockedDaysFromToday
        return diffInDays > 0 && diffInDays <= blockedDaysFromToday;
      })();

      return (
        isPastDay || isTodayBlocked || isBlockedFromToday || isDateClosed(date)
      );
    },
    [
      blockSameDayOrder,
      blockedDaysFromToday,
      getDateInfo,
      isDateClosed,
      data?.isTodayPastCutoff,
      today,
    ],
  );

  const getNextAvailableDate = useCallback(
    (startDate: Date) => {
      if (
        data?.nextAvailableDate &&
        selectedRange[0] &&
        DateTime.fromJSDate(startDate).hasSame(
          DateTime.fromJSDate(selectedRange[0]),
          'day',
        )
      ) {
        // API returns YYYY-MM-DD string; convert to local-midnight Date for the calendar
        return DateTime.fromISO(data.nextAvailableDate).toJSDate();
      }

      let currentDate = DateTime.fromJSDate(startDate);
      while (isDateUnavailable(currentDate.toJSDate())) {
        currentDate = currentDate.plus({ days: 1 });
      }
      return currentDate.toJSDate();
    },
    [data?.nextAvailableDate, isDateUnavailable, selectedRange],
  );

  const getDaysInRange = useCallback((start: Date, end: Date) => {
    let days = 0;
    let currentDate = DateTime.fromJSDate(start).startOf('day');
    const endDate = DateTime.fromJSDate(end).startOf('day');

    while (currentDate < endDate) {
      days++;
      currentDate = currentDate.plus({ days: 1 });
    }
    return days;
  }, []);

  const getDefaultDate = useCallback(() => {
    if (value?.[0]) return value[0];
    // API returns YYYY-MM-DD string; convert to local-midnight Date for the calendar
    if (data?.defaultDate) return DateTime.fromISO(data.defaultDate).toJSDate();

    if (blockSameDayOrder) {
      // Use stable date reference to avoid infinite loops
      const tomorrow = today.plus({ days: 1 }).toJSDate();
      return getNextAvailableDate(tomorrow);
    }

    return undefined;
  }, [
    blockSameDayOrder,
    data?.defaultDate,
    getNextAvailableDate,
    value,
    today,
  ]);

  useEffect(() => {
    if (onDaysOverChange && data?.daysOver !== undefined) {
      onDaysOverChange(data.daysOver);
    }
  }, [data?.daysOver, onDaysOverChange]);

  useEffect(() => {
    if (value) {
      setSelectedRange(value);
    }
  }, [value]);

  const isInRange = useCallback(
    (date: Date) => {
      const currentSelection = value || selectedRange;
      if (!currentSelection[0]) return false;

      const start = currentSelection[0];
      const end = currentSelection[1] || hoveredDate;

      if (pickupDateOptional && !currentSelection[1]) {
        return DateTime.fromJSDate(date).hasSame(
          DateTime.fromJSDate(start),
          'day',
        );
      }

      if (!end) return false;

      return (
        DateTime.fromJSDate(date) >= DateTime.fromJSDate(start) &&
        DateTime.fromJSDate(date) <= DateTime.fromJSDate(end)
      );
    },
    [hoveredDate, pickupDateOptional, selectedRange, value],
  );

  const isOverLimit = useCallback(
    (date: Date) => {
      const currentSelection = value || selectedRange;
      if (
        !daysLimit ||
        !currentSelection[0] ||
        (pickupDateOptional && !currentSelection[1])
      )
        return false;

      const start = currentSelection[0];
      const end = currentSelection[1] || hoveredDate;

      if (!end || !isInRange(date)) return false;

      // If the specific date we're checking is a closed/unavailable day, it's not "over limit"
      if (isDateClosed(date) || isDateUnavailable(date)) {
        return false;
      }

      if (countInactiveDaysAsRentalDays) {
        // Count all days including inactive days
        const rawDays = getDaysInRange(start, date);
        const totalDays = startRentalFromDeliveryDate ? rawDays + 1 : rawDays;
        return totalDays > daysLimit;
      } else {
        // Count all valid (non-closed) days from start to this specific date
        let validDayCount = 0;
        let currentDate = DateTime.fromJSDate(start).toJSDate();

        // Iterate through the date range up to and including the date we're checking
        while (DateTime.fromJSDate(currentDate) <= DateTime.fromJSDate(date)) {
          // Only count days that aren't closed
          if (!isDateClosed(currentDate)) {
            validDayCount++;
          }

          // Move to the next day
          currentDate = DateTime.fromJSDate(currentDate)
            .plus({ days: 1 })
            .toJSDate();
        }

        // Add one if rental starts on delivery date
        const totalDays = startRentalFromDeliveryDate
          ? validDayCount
          : validDayCount - 1;

        // The date is over limit if the counted valid days exceeds the limit
        return totalDays > daysLimit;
      }
    },
    [
      daysLimit,
      hoveredDate,
      isDateClosed,
      isDateUnavailable,
      isInRange,
      pickupDateOptional,
      selectedRange,
      startRentalFromDeliveryDate,
      countInactiveDaysAsRentalDays,
      getDaysInRange,
      value,
    ],
  );

  const isFirstInRange = useCallback(
    (date: Date) => {
      const currentSelection = value || selectedRange;
      if (!currentSelection[0]) return false;

      return DateTime.fromJSDate(date).hasSame(
        DateTime.fromJSDate(currentSelection[0]),
        'day',
      );
    },
    [selectedRange, value],
  );

  const isLastInRange = useCallback(
    (date: Date) => {
      const currentSelection = value || selectedRange;
      if (!currentSelection[1] || pickupDateOptional) return false;

      return DateTime.fromJSDate(date).hasSame(
        DateTime.fromJSDate(currentSelection[1]),
        'day',
      );
    },
    [pickupDateOptional, selectedRange, value],
  );

  const handleDateClick = useCallback(
    (date: Date) => {
      if (!onChange || isDateUnavailable(date)) return;

      if (pickupDateOptional) {
        // For single date selection, always set just the delivery date
        const nextDate = isDateClosed(date) ? getNextAvailableDate(date) : date;
        const newRange: [Date | null, Date | null] = [nextDate, null];
        setSelectedRange(newRange);
        onChange(newRange);
      } else {
        // For range selection
        if (!selectedRange[0] || (selectedRange[0] && selectedRange[1])) {
          const nextDate = isDateClosed(date)
            ? getNextAvailableDate(date)
            : date;
          const newRange: [Date | null, Date | null] = [nextDate, null];
          setSelectedRange(newRange);
          onChange(newRange);
          setHoveredDate(null);
        } else {
          const start = selectedRange[0];
          let end = date;

          // If the end date is before the start date, swap them
          if (start && DateTime.fromJSDate(end) < DateTime.fromJSDate(start)) {
            const newRange: [Date | null, Date | null] = [end, start];
            setSelectedRange(newRange);
            onChange(newRange);
            return;
          }

          if (isDateClosed(end)) {
            end = getNextAvailableDate(end);
          }

          const newRange: [Date | null, Date | null] = [start, end];
          setSelectedRange(newRange);
          onChange(newRange);
        }
      }
    },
    [
      getNextAvailableDate,
      isDateClosed,
      isDateUnavailable,
      onChange,
      pickupDateOptional,
      selectedRange,
    ],
  );

  return {
    isLoading,
    selectedRange,
    hoveredDate,
    setHoveredDate,
    businessHours,
    specialDates,
    forecast,
    getDateInfo,
    isDateClosed,
    isDateUnavailable,
    getNextAvailableDate,
    getDaysInRange,
    getDefaultDate,
    isTodayPastCutoff: data?.isTodayPastCutoff,
    isInRange,
    isOverLimit,
    isFirstInRange,
    isLastInRange,
    handleDateClick,
  };
}
